import { createEntityAdapter, EntityAdapter, EntityState, Dictionary } from '@ngrx/entity';
import { createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { IFermenter, IFermenterVM } from 'app/models/fermenter';
import * as Action from './actions';
import * as fromUnits from '../units/reducer';
import * as fromLocation from '../location/reducer';
import * as fromBeverage from '../beverage/reducer';
import { UnitsHelper } from 'app/services/units.helper';
import { ILocation } from 'app/models/location';
import { IBeverage } from 'app/models/beverage';
import * as fromRouter from '../router/router-state.selectors';
import * as fromTower from '../tower/reducer';

export interface State extends EntityState<IFermenter> {
  selectedId: string | null;
  error: string;
}

export function sortByOrder(a: IFermenter, b: IFermenter): number {
  const compare = (a.order ?? 0) - (b.order ?? 0);
  return compare > 0 ? 1 : compare < 0 ? -1 : 0;
}

export function selectId(a: IFermenter): string {
  return a.id ?? '';
}

export const adapter: EntityAdapter<IFermenter> = createEntityAdapter<IFermenter>({
  sortComparer: sortByOrder,
  selectId,
});

export const initialState: State = adapter.getInitialState({
  selectedId: null,
  error: '',
});

export const stateName = 'fermenter';

export const reducer = createReducer<State>(
  initialState,
  on(Action.clearAllItems, (state) => adapter.removeAll(state)),
  on(Action.loadItemsSuccess, (state, { items }) => adapter.setAll(items, state)),
  on(Action.updateItem, (state, { item }) => adapter.setOne(item, state)),
  on(
    Action.setItem,
    Action.setItemAndNavigate,
    Action.setItemFromListPage,
    (state, action) => Object.assign({ ...state, selectedId: action.id })),
);

const {
  selectAll,
  selectEntities: selectItemEntities,
} = adapter.getSelectors();

export { selectAll };

export const getSelectedId = (state: State) => state.selectedId;
const getState = createFeatureSelector<State>(stateName);
export const selectAllItems = createSelector(getState, selectAll);
export const selectAllEntities = createSelector(getState, selectItemEntities);
export const selectCurrentId = createSelector(getState, getSelectedId);
export const selectCurrentItem = createSelector(selectAllEntities, selectCurrentId, (entities, id) => {
  if (id) return entities[id];
  return null;
});
export const selectEntity = (id: string) => createSelector(selectAllEntities, entities => entities[id]);

/** Volume fields (size/amount) stored in litres, displayed via units.kegs (same as kegs). */
export const getFermentersWithUnit = createSelector(
  selectAllItems,
  fromUnits.getUnits,
  (items, units) => {
    if (!units) return items;
    const helper = new UnitsHelper();
    return items.map(item => ({
      ...item,
      size: item.size ? helper.toUnit(item.size, 'l', units.kegs, 2) : 0,
      amount: item.amount ? helper.toUnit(item.amount, 'l', units.kegs, 2) : 0,
    } as IFermenter));
  });

export const getFermentersWithBeverageAndLocation = createSelector(
  getFermentersWithUnit,
  fromBeverage.selectAllEntities,
  fromLocation.selectAllEntities,
  (items: IFermenter[], beverages: Dictionary<IBeverage>, locations: Dictionary<ILocation>) => {
    return items.map(item => ({
      ...item,
      beverage: item.beverageId ? beverages[item.beverageId] : undefined,
      location: item.locationId ? locations[item.locationId] : undefined,
    } as IFermenterVM));
  });

export const getFermenterWithUnits = (id: string) => createSelector(
  getFermentersWithBeverageAndLocation,
  (items) => items.find(x => x.id === id));

/** Current fermenter (with beverage/location + display units) resolved from the route :id. */
export const getFermenterWithUnitsFromParams = createSelector(
  fromRouter.selectRouteParams,
  getFermentersWithBeverageAndLocation,
  ({ id }, items) => items.find(x => x.id === id));

/** Fermenters placed at the location shown in location-detail (same pattern as keg/CO2). */
export const selectAllItemsFromCurrentLocation = createSelector(
  getFermentersWithBeverageAndLocation,
  fromLocation.selectCurrentId,
  (items, locationId) => items.filter(item => item.locationId === locationId));

/** Fermenters that belong to the tower shown in the detail view (tower-state selectedId). */
export const selectFermentersBySelectedTowerId = () => createSelector(
  fromTower.selectCurrentId,
  getFermentersWithBeverageAndLocation,
  (towerId, items) => towerId ? items.filter(item => item.towerId === towerId) : []);

/** Fermenters that are NOT on the selected tower — for the "Add fermenter" picker. */
export const selectFermentersNotInSelectedTower = createSelector(
  fromTower.selectCurrentId,
  getFermentersWithBeverageAndLocation,
  (towerId, items) => items.filter(item => item.towerId !== towerId));
